import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import AssetDataService from "../services/AssetService";
import config from "../../config";
import { formatApiError } from "../../utilities/apiErrorMessages";
import { coalesceAsync, invalidateCoalescedAsync } from "../../utilities/coalesceAsync";
const ASSETS_LIST_CACHE_KEY = "get-assets:";

const initialState = {
  assets: [],
  error: null,
  successMessage: null,
  loading: false,
  selectedAsset: null,
  // pagination
  total: 0,
  page: 1,
  per_page: 15,
}

export const createAsset = createAsyncThunk(
  "assets/create",
  async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
      const isFormData = obj.values instanceof FormData;
      const response = await fetch(`${config.apiUrl}/assets`, {
        method: "POST",
        body: isFormData ? obj.values : JSON.stringify(obj.values),
        headers: {
          'Accept': "application/json",
          ...(isFormData ? {} : { "Content-Type": "application/json" }),
          'Authorization': `Bearer ${obj.token}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data)
      } else {
          invalidateCoalescedAsync(ASSETS_LIST_CACHE_KEY)
        return fulfillWithValue(data)
        invalidateCoalescedAsync(ASSETS_LIST_CACHE_KEY)
      }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
);

export const retrieveAssets = createAsyncThunk(
  "assets/retrieve",
  async (obj = {}, thunkAPI) => {
    try {
      // support retrieveAssets({ page, per_page, search, name, notes })
      const params = { ...(obj || {}) };
      const token = obj?.token || null;
      if (token) delete params.token;

      const response = await coalesceAsync(
        `get-assets:${JSON.stringify(params)}`,
        () => AssetDataService.getAll(params),
        { ttlMs: 1500 }
      );
      const payload = response?.data;

      // Nested paginated shape: { data: { data, total, per_page, current_page }, message }
      if (Array.isArray(payload?.data?.data)) {
        const page = payload.data;
        return {
          data: page.data,
          total: page.total ?? page.data.length,
          per_page: page.per_page ?? null,
          current_page: page.current_page ?? 1,
          last_page: page.last_page ?? null,
        };
      }

      // Flat paginator: { data: [...], total, per_page, current_page }
      if (
        payload &&
        Array.isArray(payload.data) &&
        (payload.total != null || payload.current_page != null || payload.per_page != null)
      ) {
        return {
          data: payload.data,
          total: payload.total ?? payload.data.length,
          per_page: payload.per_page ?? null,
          current_page: payload.current_page ?? 1,
          last_page: payload.last_page ?? null,
        };
      }

      if (Array.isArray(payload)) {
        return { data: payload, total: payload.length, current_page: 1, per_page: payload.length };
      }

      if (Array.isArray(payload?.data)) {
        return {
          data: payload.data,
          total: payload.total ?? payload.data.length,
          per_page: payload.per_page ?? null,
          current_page: payload.current_page ?? 1,
          last_page: payload.last_page ?? null,
        };
      }

      return { data: [], total: 0, current_page: 1, per_page: 10 };
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.message ?? "Unable to fetch assets");
    }
  }
);

export const updateAsset = createAsyncThunk(
  "assets/update",
  async (obj, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI
    try {
      const isFormData = obj.payload instanceof FormData;
      const id = isFormData ? obj.id : obj.payload.id;
      // PHP only parses multipart bodies on POST, so file updates spoof PUT.
      if (isFormData && !obj.payload.get("_method")) {
        obj.payload.append("_method", "PUT");
      }
      const response = await fetch(`${config.apiUrl}/assets/${id}`, {
        method: isFormData ? "POST" : "PUT",
        body: isFormData ? obj.payload : JSON.stringify(obj.payload),
        headers: {
          'Accept': "application/json",
          ...(isFormData ? {} : { "Content-Type": "application/json" }),
          'Authorization': `Bearer ${obj.token}`
        }
      });
      const data = await response.json();
      if (!response.ok) {
        return rejectWithValue(data)
      } else {
        return fulfillWithValue(data)
      }

    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
);

export const deleteAsset = createAsyncThunk(
  "assets/delete",
  async ({ id }, thunkAPI) => {
    const { rejectWithValue, fulfillWithValue } = thunkAPI;
    try {
      const response = await AssetDataService.remove(id);
      const data = response.data ?? {};
      if (response.status >= 400) {
        return rejectWithValue(data);
        invalidateCoalescedAsync(ASSETS_LIST_CACHE_KEY)
      }
      return fulfillWithValue({
        id,
        message: data?.message ?? null,
      });
    } catch (error) {
      return rejectWithValue(error?.response?.data ?? { message: error?.message ?? "Unable to delete asset" });
    }
  }
);
const assetSlice = createSlice({
  name: "asset",
  initialState,
  reducers: {
    selectAsset: (state, action) => {
      state.selectedAsset = action.payload
    },
    clearAssetFeedback: (state) => {
      state.error = null
      state.successMessage = null
    }
  },
  extraReducers: {
    // retrieveAssets actions
    [retrieveAssets.pending]: (state, action) => {
      state.loading = true
      state.successMessage = null
      state.error = null
    },
    [retrieveAssets.fulfilled]: (state, action) => {
      state.loading = false
      state.assets = action.payload?.data ?? [];
      state.total = action.payload?.total ?? (Array.isArray(action.payload?.data) ? action.payload.data.length : 0);
      state.page = action.payload?.current_page ?? state.page;
      state.per_page = action.payload?.per_page ?? state.per_page;
      state.successMessage = null
      state.error = null
    },
    [retrieveAssets.rejected]: (state, action) => {
      state.loading = false
      state.successMessage = null
      state.error = formatApiError(action.payload, 'Unable to fetch assets')
    },

    // createAsset actions
    [createAsset.pending]: (state, action) => {
      state.loading = true;
      state.successMessage = null
      state.error = null
    },
    [createAsset.fulfilled]: (state, action) => {
      state.loading = false
      state.successMessage = action.payload.message
      state.error = null
    },
    [createAsset.rejected]: (state, action) => {
      state.loading = false
      state.successMessage = null
      state.error = formatApiError(action.payload, 'Unable to create asset')
    },

    // updateAsset actions
    [updateAsset.pending]: (state, action) => {
      state.loading = true;
      state.successMessage = null
      state.error = null
    },
    [updateAsset.fulfilled]: (state, action) => {
      const index = state.assets.findIndex(asset => asset.id === action.payload.data.id);
      state.assets[index] = {
        ...state.assets[index],
        ...action.payload.data,
      };
      state.loading = false;
      state.successMessage = action.payload.message
      state.error = null
    },
    [updateAsset.rejected]: (state, action) => {
      state.loading = false
      state.successMessage = null
      state.error = formatApiError(action.payload, 'Unable to update asset')
    },

    // deleteAsset actions
    [deleteAsset.pending]: (state, action) => {
      state.loading = true;
      state.successMessage = null
      state.error = null
    },
    [deleteAsset.fulfilled]: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload?.message ?? null;
      state.error = null;
      const id = action.payload?.id;
      if (id != null) {
        state.assets = (state.assets ?? []).filter((asset) => asset.id !== id);
        state.total = Math.max(0, (state.total ?? 0) - 1);
      }
    },
    [deleteAsset.rejected]: (state, action) => {
      state.loading = false;
      state.successMessage = null
      state.error = formatApiError(action.payload, 'Unable to delete asset')
    },
  },
});

const { reducer } = assetSlice;
export const { selectAsset, clearAssetFeedback } = assetSlice.actions
export default reducer;